import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, useWatch } from "react-hook-form";

import { Breadcrumbs } from "./components/Breadcrumbs.jsx";
import { ErrorAlert } from "./components/ErrorAlert.jsx";
import { OptionalFieldsBlock } from "./components/OptionalFieldsBlock.jsx";
import { isLineItemAvailable, OrderLineItem } from "./OrderLineItem.jsx";
import { lineItemId, safeLineItemKey, useWithdrawalWizardContext } from "./useWithdrawalWizard.js";
import { readProxyLocale } from "./utils/proxyLocale.js";

export function OrderItemsStep({ optionalFields, designMode }) {
  const { t } = useTranslation();
  const { control, handleSubmit } = useFormContext();
  const { error, goToCustomer, loading, orderPayload, submitOrder } = useWithdrawalWizardContext();
  const order = orderPayload.order || {};
  const lineItems = orderPayload.line_items || [];
  const lineItemAvailability = orderPayload.line_item_availability || {};
  const withdrawalReasons = orderPayload.withdrawal_reasons || [];
  const selectedLineItems = useWatch({ control, name: "line_item_selected" }) || {};
  const createdLabel = useMemo(() => {
    const raw = order.created_at || order.createdAt || order.shopify_order_created_at;
    if (!raw) return "";
    const date = new Date(String(raw));
    if (Number.isNaN(date.getTime())) return "";

    return new Intl.DateTimeFormat(readProxyLocale() === "en" ? "en-US" : "de-DE", {
      dateStyle: "long",
    }).format(date);
  }, [order]);
  const submitEnabled = lineItems.some((item) => {
    const id = lineItemId(item);
    return selectedLineItems[safeLineItemKey(id)] && isLineItemAvailable(item, lineItemAvailability[id] || {});
  });
  const allLineItemsUnavailable =
    lineItems.length > 0 &&
    lineItems.every((item) => {
      const id = lineItemId(item);
      return !isLineItemAvailable(item, lineItemAvailability[id] || {});
    });

  return (
    <div className="wp:mx-auto wp:max-w-xl">
      <Breadcrumbs current={2} />
      <h1 className="wp:mb-6 wp:text-3xl wp:font-normal wp:tracking-tight wp:[color:var(--wf-text-color,#111827)] wp:md:text-4xl">
        {t("proxy.withdrawals.order.title")}
      </h1>
      <ErrorAlert error={error} />
      {allLineItemsUnavailable ? (
        <div className="wp:mb-6 wp:rounded-md wp:border wp:border-solid wp:border-amber-200 wp:bg-amber-50 wp:p-4 wp:text-base wp:text-amber-900">
          {t("proxy.withdrawals.order.all_items_unavailable")}
        </div>
      ) : null}
      <div className="wp:mb-8 wp:rounded-md wp:border wp:border-solid wp:border-gray-200 wp:bg-gray-50 wp:p-6">
        <dl className="wp:grid wp:grid-cols-2 wp:gap-4">
          <Summary label={t("proxy.withdrawals.common.fields.order_number")}>
            {order.shopify_order_number || order.shopifyOrderNumber || order.name}
          </Summary>
          <Summary label={t("proxy.withdrawals.common.fields.order_date")}>{createdLabel}</Summary>
        </dl>
      </div>
      <p className="wp:mb-6 wp:text-lg wp:[color:var(--wf-text-color,#111827)]">{t("proxy.withdrawals.order.choose_items")}</p>

      <form onSubmit={handleSubmit(submitOrder)}>
        <ul className="wp:mb-8 wp:grid wp:gap-4 wp:p-0">
          {lineItems.map((item) => {
            const id = lineItemId(item);

            return (
              <OrderLineItem
                key={id}
                item={item}
                availability={lineItemAvailability[id] || {}}
                withdrawalReasons={withdrawalReasons}
              />
            );
          })}
        </ul>

        <OptionalFieldsBlock flags={optionalFields} />
        <p className="wp:mb-8 wp:rounded-md wp:bg-gray-50 wp:p-4 wp:text-sm wp:text-gray-600">
          {t("proxy.withdrawals.order.declaration")}
        </p>
        <div className="wp:flex wp:flex-col wp:gap-4">
          <button
            type="submit"
            disabled={!submitEnabled || loading || designMode}
            className="wp:w-full wp:rounded-md wp:border wp:border-solid wp:[border-color:var(--wf-button-border,#111827)] wp:[background-color:var(--wf-button-bg,#111827)] wp:px-6 wp:py-4 wp:text-lg wp:font-medium wp:[color:var(--wf-button-text,#ffffff)] wp:shadow-sm wp:transition-opacity wp:enabled:cursor-pointer wp:hover:opacity-90 wp:disabled:cursor-not-allowed wp:disabled:opacity-50 wp:sm:flex-1"
          >
            {t("proxy.withdrawals.common.actions.submit")}
          </button>
          <button type="button" onClick={goToCustomer} className="wp:w-full wp:cursor-pointer wp:rounded-md wp:border wp:border-solid wp:border-gray-300 wp:bg-white wp:px-6 wp:py-4 wp:text-lg wp:font-medium wp:text-gray-700">
            {t("proxy.withdrawals.common.actions.back")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Summary({ label, children }) {
  return (
    <div>
      <dt className="wp:text-sm wp:font-medium wp:text-gray-500">{label}</dt>
      <dd className="wp:mt-1 wp:text-lg wp:[color:var(--wf-text-color,#111827)]">{children}</dd>
    </div>
  );
}
